'use client';

import { LogIn } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Analytics } from '@/features/common/analytics';
import { useAuth } from '@/shared/auth';

// 네비게이션 아이콘 이름 타입
type NavIconName = 'home' | 'challenge' | 'saved' | 'leaderboard' | 'my';

// 네비게이션 아이템 타입 정의
type NavItem = {
  name: string;
  href: string;
  iconName?: NavIconName;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
};

// 네비게이션 아이콘 렌더링 함수
function renderNavIcon(item: NavItem, isActive: boolean) {
  if ('iconName' in item && item.iconName) {
    // Active: outlined-black, Inactive: filled-black
    const iconVariant = isActive ? 'outlined' : 'filled';
    const iconPath = `/assets/icons/navigation/${item.iconName}-${iconVariant}-black.svg`;

    return (
      <div
        className={`relative shrink-0 transition-all duration-200 ease-out ${
          isActive
            ? '-translate-y-0.5 scale-100 animate-[navChewy_320ms_cubic-bezier(0.22,1,0.36,1)_1]'
            : 'translate-y-0 scale-95'
        }`}
        style={{
          opacity: isActive ? 1 : 0.8,
        }}
      >
        <Image
          src={iconPath}
          width={26}
          height={26}
          alt={item.name}
          className={isActive ? '' : 'brightness-0 saturate-0'}
          style={{
            filter: isActive
              ? 'none'
              : 'brightness(0) saturate(100%) invert(63%) sepia(6%) saturate(407%) hue-rotate(177deg) brightness(90%) contrast(88%)',
          }}
        />
      </div>
    );
  }

  if ('icon' in item && item.icon) {
    const IconComponent = item.icon;
    return (
      <IconComponent
        className={`relative h-[26px] w-[26px] shrink-0 transition-all duration-200 ease-out ${
          isActive
            ? '-translate-y-0.5 scale-100 animate-[navChewy_320ms_cubic-bezier(0.22,1,0.36,1)_1] text-semantic-label-strong opacity-100'
            : 'translate-y-0 scale-95 text-atomic-cool-neutral-300 opacity-80'
        }`}
        strokeWidth={isActive ? 2.5 : 2}
      />
    );
  }

  return null;
}

export default function BottomNav() {
  const pathname = usePathname();
  // 선택적 구독: user와 isLoading만 필요
  const user = useAuth(s => s.user);
  const isAuthLoading = useAuth(s => s.isLoading);
  const { t } = useTranslation();

  // Do not render anything while loading the session to prevent flickering
  if (isAuthLoading) {
    return null;
  }

  const baseNavItems = [
    {
      name: t('NaviFooter.mission'),
      href: '/',
      iconName: 'home' as const,
    },
    {
      name: t('NaviFooter.explore'),
      href: '/challenges',
      iconName: 'challenge' as const,
    },
    {
      name: t('NaviFooter.saved'),
      href: '/saved',
      iconName: 'saved' as const,
    },
    {
      name: t('NaviFooter.leaderboard'),
      href: '/leaderboard',
      iconName: 'leaderboard' as const,
    },
  ];

  const navItems = [
    ...baseNavItems,
    ...(user
      ? [
          {
            name: t('NaviFooter.my'),
            href: '/mypage',
            iconName: 'my' as const,
          },
        ]
      : [
          {
            name: t('Auth.login'),
            href: '/login',
            icon: LogIn,
          },
        ]),
  ];

  // 메뉴 개수에 따른 너비 계산 (5개)
  const menuWidth = '19%';

  // 현재 페이지 재클릭 시 스크롤 최상단 이동
  const handleCurrentPageClick = (href: string, e: React.MouseEvent) => {
    if (pathname === href) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Go to Top 이벤트 추적
      Analytics.track('Click Navigation Go To Top', {
        tab_name: navItems.find(item => item.href === href)?.name,
        current_path: pathname,
      });
    }
    // 다른 페이지로 이동하는 경우는 Link의 기본 동작 사용
  };

  // 네비게이션 클릭 이벤트 추적
  const handleNavClick = (tabName: string, href: string) => {
    // 현재 페이지가 아닌 경우에만 일반 네비게이션 이벤트 추적
    if (pathname !== href) {
      Analytics.track('Click Navigation Tab', {
        tab_name: tabName,
        target_path: href,
        current_path: pathname,
      });
    }
  };

  return (
    <nav className="fixed right-0 bottom-0 left-0 z-50 h-[58px] bg-atomic-neutral-100">
      <div className="mx-auto flex h-full max-w-screen-md items-center">
        {/* Left padding - 2.5% */}
        <div className="w-[2.5%]" />

        {navItems.map(item => {
          const isActive = pathname === item.href;
          return (
            <Link
              href={item.href}
              key={item.name}
              prefetch={false}
              style={{ width: menuWidth }}
              onClick={e => {
                handleCurrentPageClick(item.href, e);
                handleNavClick(item.name, item.href);
              }}
            >
              <div className="relative box-border flex size-full flex-col content-stretch items-center justify-start gap-0 px-0 pt-2 pb-2.5 transition-transform duration-200 ease-out motion-safe:active:scale-95">
                {renderNavIcon(item as NavItem, isActive)}
                <div
                  className={`relative shrink-0 text-nowrap text-center font-medium text-[11px] leading-[0] transition-colors duration-200 ease-out ${
                    isActive ? 'text-semantic-label-strong' : 'text-atomic-cool-neutral-300'
                  }`}
                  style={{
                    fontVariationSettings: "'opsz' 14",
                  }}
                >
                  <p className="block whitespace-pre leading-[1.273]">{item.name}</p>
                </div>
              </div>
            </Link>
          );
        })}

        {/* Right padding - 2.5% */}
        <div className="w-[2.5%]" />
      </div>
    </nav>
  );
}
